/*
 * analyzer.js — первый проход /video-stabilizer: движение камеры по всему ролику.
 *
 *   VSAnalyzer.run({ file, W, H, workerUrl, signal, onProgress, onPair })
 *     → { pairs, times, frames, pool, ms }
 *
 * Кадры даёт WebCodecsPipeline.run в режиме analyzeOnly — ТОТ ЖЕ конвейер, что
 * потом собирает файл. Отсюда два свойства, которых у прежнего пути через
 * экстрактор не было:
 *   · нумерация кадров обоих проходов совпадает по построению (поправка
 *     выбирается по номеру кадра — см. correctionAt в блейде);
 *   · открывается всё, что открывает сборка: mp4/mov И WebM/MKV. Экстрактор
 *     читал только семейство mp4 и на WebM отвечал «браузер не может».
 *
 * Кадр уменьшается на GPU (drawVideo в холст конвейера, два размера), а
 * математика — FFT и RANSAC, ~8–15 мс на кадр — уходит в пул воркеров
 * (analyze-worker.js). До 30.09 всё это шло в главном потоке, ~42 мс на кадр:
 * минута 1080p30 — 76 секунд с замершей страницей.
 *
 * Раздача ЧАНКАМИ по C кадров: чанк k идёт воркеру k mod N, а его последняя
 * пара (кадр (k+1)·C) требует первого кадра СЛЕДУЮЩЕГО чанка — он уходит
 * обоим воркерам (второму — как `start`). Так у каждого воркера свой
 * непрерывный кусок и ни одного кадра, подготовленного дважды, кроме стыков.
 *
 * Противодавление: кадров «в работе» не больше (N + 1)·C; сверх того draw()
 * возвращает обещание, и конвейер ждёт его, прежде чем взять следующий кадр
 * (geometry2d.analyzeOnly, _anaHold в webcodecs-pipeline.js). Без этого
 * аппаратный декодер (200–400 кадр/с) обгонял пул, и очередь росла без
 * предела.
 *
 * Нет воркеров (или пул не поднялся) — тот же analyseFrame в главном потоке:
 * медленнее, но тем же кодом.
 */
(function () {
'use strict';
if (window.VSAnalyzer) return;

var CHUNK = 16;

function poolSize() {
    var hc = navigator.hardwareConcurrency || 4;
    var n = Math.max(1, Math.min(6, hc - 2));
    // Слабое устройство: воркеров меньше — главному потоку и декодеру тоже
    // нужно ядро, а памяти на очередь меньше. Число памяти — у владельца
    // (window.__memBudget): iOS прячет deviceMemory, но вкладку WebKit
    // убивает свой потолок, и там это тоже «слабое».
    var B = window.__memBudget;
    if (B && (B.gb() <= 2 || B.constrained())) n = Math.min(n, 2);
    return n;
}

/* Часы видимого времени (timbrica-core.js): скрытая вкладка медиа не грузит, и
   сторож по стенным часам объявил бы мёртвым пул, который просто ждал вкладку.
   Возвращает функцию-отмену. */
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var id = setTimeout(fn, ms);
    return function () { clearTimeout(id); };
}
function cancelTimer(t) {
    if (typeof t === 'function') { try { t(); } catch (_) {} }
}

/* Поднять пул СТУПЕНЬКОЙ: первый воркер тянет свои два скрипта по сети,
   остальные стартуют, когда он готов, и берут их из кэша (версионированные
   ответы immutable). Все шесть разом — это двенадцать одинаковых запросов в
   одну секунду: локальный стенд под нагрузкой ронял на этом importScripts с
   NetworkError, и пул, прождав 20 с, сдавался в главный поток (30.09; тот же
   урок у /video-retouch, 26.09). Пул — те, кто поднялся: не встал первый — пула
   нет вовсе, главный поток справится тем же кодом; не встали некоторые из
   остальных — работаем без них. */
function startPool(url, n, cfg) {
    return new Promise(function (resolve) {
        var ready = [], spawned = 0, failed = 0, settled = false, timer = null, grace = null;
        function finish() {
            if (settled) return;
            settled = true;
            cancelTimer(timer); cancelTimer(grace);
            resolve(ready.length ? ready.slice() : null);
        }
        function spawn(first) {
            var w;
            spawned++;
            try { w = new Worker(url); } catch (_) { failed++; if (first) finish(); return; }
            var up = false;
            w.onmessage = function (e) {
                var m = e.data || {};
                if (m.cmd === 'ready' && !up) {
                    up = true;
                    if (settled) { try { w.terminate(); } catch (_) {} return; }
                    ready.push(w);
                    if (first) {
                        for (var i = 1; i < n; i++) spawn(false);
                        if (n === 1) { finish(); return; }
                        // Остальным — немного времени: они грузятся из кэша.
                        grace = visibleTimeout(finish, 4000);
                    } else if (ready.length + failed >= n) finish();
                } else if (m.cmd === 'error' && !up) { down(); }
            };
            w.onerror = function (ev) { try { ev.preventDefault(); } catch (_) {} if (!up) down(); };
            function down() {
                up = true;
                failed++;
                try { w.terminate(); } catch (_) {}
                if (first) finish();
                else if (ready.length + failed >= n) finish();
            }
            w.postMessage({ cmd: 'init', cfg: cfg });
        }
        timer = visibleTimeout(finish, 20000);
        spawn(true);
    });
}

async function run(o) {
    var VS = window.VideoStabilizer, R = window.RtMotion, P = window.WebCodecsPipeline;
    if (!VS || !R || !P) throw new Error('vs-analyze-missing-modules');
    var t0 = performance.now();
    var W = o.W, H = o.H, cfg = VS.analyseConfig(W, H);
    var n = (o.workerUrl && typeof Worker !== 'undefined' && o.pool !== 0) ? (o.pool || poolSize()) : 0;
    /* Куда уходит время — в итог прохода (и в телеметрию): старт пула, наше
       рисование в draw, ожидание пула (противодавление) и дослив хвоста. */
    var st = { startMs: 0, drawMs: 0, holdMs: 0, drainMs: 0, holds: 0 };
    var workers = n ? await startPool(o.workerUrl, n, cfg) : null;
    st.startMs = Math.round(performance.now() - t0);
    if (workers) n = workers.length; else n = 0;
    // Один воркер — один чанк на весь ролик: стыки ему ни к чему.
    var C = n > 1 ? CHUNK : Infinity;
    var LIMIT = n ? (n + 1) * (n > 1 ? CHUNK : CHUNK * 2) : 0;

    var pairs = [], times = [], decoded = 0, done = 0, inflight = 0;
    var failure = null, waiter = null, idleWaiter = null, prevInline = null, stall = null;
    var lastBeat = performance.now(), lastReport = 0, estTotal = o.frames || 0;

    function fail(err) {
        if (failure) return;
        failure = err;
        if (waiter) { var w = waiter; waiter = null; w.reject(err); }
        if (idleWaiter) { var iw = idleWaiter; idleWaiter = null; iw.reject(err); }
    }
    function report(force) {
        var now = performance.now();
        if (!force && now - lastReport < 120) return;
        lastReport = now;
        if (typeof o.onProgress === 'function') {
            try { o.onProgress({ decoded: decoded, done: done, total: Math.max(estTotal, decoded) }); } catch (_) {}
        }
    }
    function armStall() {
        cancelTimer(stall);
        if (!n || inflight <= 0) { stall = null; return; }
        /* Сторож тишины: пул, который 30 с видимого времени не ответил ни на
           один кадр, мёртв (Android замораживает воркеры, падение воркера
           приходит не всегда). Перевзводится каждым ответом. */
        stall = visibleTimeout(function () { fail(new Error('vs-analyze-stall')); }, 30000);
    }
    function onMessage(e) {
        var m = e.data || {};
        if (m.cmd === 'error') { fail(new Error('vs-analyze-worker: ' + m.message)); return; }
        if (m.cmd !== 'pair' && m.cmd !== 'free') return;
        if (m.cmd === 'pair') {
            pairs[m.i - 1] = m.p;
            done++;
            if (typeof o.onPair === 'function') { try { o.onPair(m.i, m.p); } catch (_) {} }
        }
        inflight--;
        lastBeat = performance.now();
        armStall();
        if (waiter && inflight <= LIMIT) { var w = waiter; waiter = null; w.resolve(); }
        if (idleWaiter && inflight <= 0) { var iw = idleWaiter; idleWaiter = null; iw.resolve(); }
        report(false);
    }
    if (workers) workers.forEach(function (w) {
        w.onmessage = onMessage;
        w.onerror = function (ev) { fail(new Error('vs-analyze-worker: ' + ((ev && ev.message) || 'failed'))); };
    });
    function onAbort() { fail(new DOMException('cancelled', 'AbortError')); }
    if (o.signal) {
        if (o.signal.aborted) onAbort();
        else o.signal.addEventListener('abort', onAbort, { once: true });
    }
    function post(k, idx, start, a, b) {
        inflight++;
        workers[k].postMessage({ cmd: 'frame', i: idx, start: start, rgba: a.buffer, rrgba: b.buffer }, [a.buffer, b.buffer]);
    }

    /* Уменьшение кадра — ДВА этапа, и оба выбраны замером против истины
       фикстуры (30.09, ошибка переноса на shaky-480x360):
         путь экстрактора (кадр целиком → CPU-холст, 'high')      0.357 px
         один шаг на GPU-холсте, 'high'                            0.445 px
       GPU-фильтр грубее CPU-шного даже с 'high' — часть строк исходника не
       участвует, и оценщик отвечает шумом. Но нести кадр 1080p на CPU целиком
       стоит 36 мс (замер drawImage VideoFrame → CPU-холст). Поэтому: грубые
       шаги — на GPU и ровно ВДВОЕ (билинейно вдвое = честное среднее 2×2),
       пока следующий уровень ещё не меньше цели; затем ОДНО чтение этого
       маленького кадра с GPU и последний шаг на CPU с 'high', как у старого
       пути. Два чтения с GPU (по одному на размер) стоили 39 мс на кадр
       1080×1920 — одно и маленькое стоит единицы. */
    var TW = Math.max(cfg.estW, cfg.rotW), TH = Math.max(cfg.estH, cfg.rotH);
    var chain = [], lw = W, lh = H;
    while (Math.round(lw / 2) >= TW && Math.round(lh / 2) >= TH) {
        lw = Math.round(lw / 2); lh = Math.round(lh / 2);
        chain.push({ w: lw, h: lh });
    }
    // Первый шаг рисует конвейер (он знает поворот кадра) — в свой холст:
    // уровень пирамиды или кадр целиком, если пирамида не нужна.
    var bw = chain.length ? chain[0].w : W, bh = chain.length ? chain[0].h : H;
    var cw = bw, ch = bh;
    var levels = chain.slice(1).map(function (l) {
        var c = new OffscreenCanvas(l.w, l.h);
        var x = c.getContext('2d', { alpha: false });
        x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
        return { w: l.w, h: l.h, c: c, x: x };
    });
    // Последний шаг — CPU-холсты: база (одно чтение с GPU) и итог — оба
    // размера разбора один под другим, чтение итога уже без GPU.
    var baseW = chain.length ? chain[chain.length - 1].w : W, baseH = chain.length ? chain[chain.length - 1].h : H;
    var baseC = new OffscreenCanvas(baseW, baseH);
    var baseX = baseC.getContext('2d', { alpha: false, willReadFrequently: true });
    var outC = new OffscreenCanvas(TW, cfg.estH + TH);
    var outX = outC.getContext('2d', { alpha: false, willReadFrequently: true });
    outX.imageSmoothingEnabled = true; outX.imageSmoothingQuality = 'high';
    try {
        await P.run({
            slug: o.slug || 'video-stabilizer', file: o.file, params: {}, crf: 23,
            abortSignal: o.signal,
            geometry2d: {
                // Холст только для уменьшения: оба размера разбора — в его угол.
                // Кодер требует чётных сторон даже там, где ничего не кодирует.
                width: cw + (cw & 1), height: ch + (ch & 1), analyzeOnly: true,
                draw: function (gctx, drawVideo, _cw, _ch, idx, tSec) {
                    if (failure) return Promise.reject(failure);
                    var td = performance.now();
                    times[idx] = tSec;
                    /* Уменьшение билинейным «low» — это алиасинг: часть строк
                       исходника не участвует, и оценщик отвечает шумом. Замер
                       05.08: без 'high' угол расходился с истиной до 0.42°
                       против 0.11°. */
                    gctx.imageSmoothingEnabled = true;
                    gctx.imageSmoothingQuality = 'high';
                    drawVideo(0, 0, W, H, 0, 0, bw, bh);
                    var src = gctx.canvas, sw = bw, sh = bh;
                    for (var li = 0; li < levels.length; li++) {
                        var L = levels[li];
                        L.x.drawImage(src, 0, 0, sw, sh, 0, 0, L.w, L.h);
                        src = L.c; sw = L.w; sh = L.h;
                    }
                    baseX.drawImage(src, 0, 0, sw, sh, 0, 0, baseW, baseH);
                    outX.drawImage(baseC, 0, 0, baseW, baseH, 0, 0, cfg.estW, cfg.estH);
                    outX.drawImage(baseC, 0, 0, baseW, baseH, 0, cfg.estH, cfg.rotW, cfg.rotH);
                    var all = outX.getImageData(0, 0, TW, cfg.estH + TH).data;
                    var a = new Uint8ClampedArray(cfg.estW * cfg.estH * 4);
                    var b = new Uint8ClampedArray(cfg.rotW * cfg.rotH * 4);
                    for (var ry = 0, rowA = cfg.estW * 4; ry < cfg.estH; ry++) {
                        a.set(all.subarray(ry * TW * 4, ry * TW * 4 + rowA), ry * rowA);
                    }
                    for (var ry2 = 0, rowB = cfg.rotW * 4; ry2 < cfg.rotH; ry2++) {
                        var off = (cfg.estH + ry2) * TW * 4;
                        b.set(all.subarray(off, off + rowB), ry2 * rowB);
                    }
                    decoded = idx + 1;
                    if (!workers) {
                        var r = VS.analyseFrame(R, a, b, prevInline, cfg, idx);
                        prevInline = r.state;
                        if (r.pair) {
                            pairs[idx - 1] = r.pair; done++;
                            if (typeof o.onPair === 'function') { try { o.onPair(idx, r.pair); } catch (_) {} }
                        }
                        report(false);
                        st.drawMs += performance.now() - td;
                        return;
                    }
                    var k = C === Infinity ? 0 : Math.floor(idx / C);
                    if (idx > 0 && C !== Infinity && idx % C === 0) {
                        // Стык: кадр замыкает последнюю пару прошлого чанка.
                        post((k - 1) % n, idx, false, new Uint8ClampedArray(a), new Uint8ClampedArray(b));
                    }
                    post(k % n, idx, idx === 0 || (C !== Infinity && idx % C === 0), a, b);
                    armStall();
                    report(false);
                    st.drawMs += performance.now() - td;
                    if (inflight > LIMIT) {
                        var th = performance.now();
                        st.holds++;
                        return new Promise(function (resolve, reject) {
                            waiter = {
                                resolve: function () { st.holdMs += performance.now() - th; resolve(); },
                                reject: reject
                            };
                        });
                    }
                }
            },
            onProgress: function (p) {
                // Сколько кадров всего — из доли, которую отдаёт конвейер.
                if (p > 0.02 && decoded > 0) estTotal = Math.max(estTotal, Math.round(decoded / p));
            }
        });
        if (failure) throw failure;
        if (workers && inflight > 0) {
            var tq = performance.now();
            await new Promise(function (resolve, reject) { idleWaiter = { resolve: resolve, reject: reject }; });
            st.drainMs = Math.round(performance.now() - tq);
        }
        if (failure) throw failure;
    } finally {
        cancelTimer(stall);
        if (o.signal) { try { o.signal.removeEventListener('abort', onAbort); } catch (_) {} }
        if (workers) workers.forEach(function (w) { try { w.terminate(); } catch (_) {} });
    }
    // Дыра в парах — это не «ровный кусок», а потерянный ответ: молча
    // склеенная траектория дала бы рывок на ровном месте.
    for (var i = 0; i < decoded - 1; i++) {
        if (!pairs[i]) throw new Error('vs-analyze-hole:' + (i + 1));
    }
    pairs.length = Math.max(0, decoded - 1);
    report(true);
    st.drawMs = Math.round(st.drawMs); st.holdMs = Math.round(st.holdMs);
    return { pairs: pairs, times: times, frames: decoded, pool: n, ms: Math.round(performance.now() - t0), stats: st };
}

window.VSAnalyzer = { run: run, poolSize: poolSize };
})();
